import React, { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import { FundSource } from './types';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';

/**
 * AI SUMMARY:
 * The FundOrderModal component provides a form for creating new fund orders.
 * Features:
 * - Select order type (market, limit, stop, stop_limit)
 * - Select order side (buy, sell)
 * - Input symbol, quantity, and price
 * - Select source from available fund sources
 * - Form validation with error handling
 * - Success and error notifications
 *
 * The component handles the API integration for submitting new orders
 * and provides feedback to the user on the order creation process.
 */

interface FundOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  fundId: string;
  sources: FundSource[];
  onOrderCreated: () => void;
}

const FundOrderModal: React.FC<FundOrderModalProps> = ({
  isOpen,
  onClose,
  fundId,
  sources,
  onOrderCreated,
}) => {
  const [symbol, setSymbol] = useState('');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [type, setType] = useState<'market' | 'limit' | 'stop' | 'stop_limit'>(
    'market',
  );
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [duration, setDuration] = useState<'day' | 'gtc' | 'gfd'>('day');
  const [sourceId, setSourceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { showNotification } = useNotification();

  // Function to validate form and get order data
  const getOrderData = (isPreview: boolean = false) => {
    if (!symbol) {
      showNotification('Symbol is required', 'error');
      return null;
    }

    if (!quantity || isNaN(Number(quantity)) || Number(quantity) <= 0) {
      showNotification('Valid quantity is required', 'error');
      return null;
    }

    if (
      (type === 'limit' || type === 'stop_limit') &&
      (!limitPrice || isNaN(Number(limitPrice)) || Number(limitPrice) <= 0)
    ) {
      showNotification(
        'Valid limit price is required for this order type',
        'error',
      );
      return null;
    }

    if (
      (type === 'stop' || type === 'stop_limit') &&
      (!stopPrice || isNaN(Number(stopPrice)) || Number(stopPrice) <= 0)
    ) {
      showNotification(
        'Valid stop price is required for this order type',
        'error',
      );
      return null;
    }

    if (!sourceId) {
      showNotification('Please select a fund source', 'error');
      return null;
    }

    const selectedSource = sources.find((source) => source.id === sourceId);
    if (!selectedSource) {
      showNotification('Selected source not found', 'error');
      return null;
    }

    return {
      symbol: symbol.toUpperCase(),
      side,
      type,
      quantity: Number(quantity),
      limitPrice:
        type === 'limit' || type === 'stop_limit'
          ? Number(limitPrice)
          : undefined,
      stopPrice:
        type === 'stop' || type === 'stop_limit'
          ? Number(stopPrice)
          : undefined,
      duration,
      sourceId: selectedSource.sourceId,
      sourceType: selectedSource.sourceType,
      isPreview,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const orderData = getOrderData(false);
    if (!orderData) return;

    try {
      setLoading(true);

      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);
      const response = await apiService.createFundOrder(fundId, orderData);

      showNotification('Order created successfully', 'success');
      setLoading(false);

      // Close modal and refresh orders after a short delay
      setTimeout(() => {
        onOrderCreated();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating order:', error);
      showNotification('Failed to create order. Please try again.', 'error');
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    const orderData = getOrderData(true);
    if (!orderData) return;

    try {
      setLoading(true);

      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);
      const response = await apiService.createFundOrder(fundId, orderData);

      // Show preview response
      showNotification('Order preview generated successfully', 'success');
      setShowPreview(true);
      setLoading(false);
    } catch (error) {
      console.error('Error previewing order:', error);
      showNotification('Failed to preview order. Please try again.', 'error');
      setLoading(false);
    }
  };

  // Reset form when modal is opened
  useEffect(() => {
    if (isOpen) {
      setSymbol('');
      setSide('buy');
      setType('market');
      setQuantity('');
      setLimitPrice('');
      setStopPrice('');
      setDuration('day');
      setSourceId('');
      setShowPreview(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedSource = sources.find((source) => source.id === sourceId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Create New Order</h2>

        <form onSubmit={handleSubmit}>
          {/* Symbol */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Symbol
            </label>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter symbol (e.g., AAPL)"
            />
          </div>

          {/* Order Side */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Side
            </label>
            <select
              value={side}
              onChange={(e) => setSide(e.target.value as 'buy' | 'sell')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>

          {/* Order Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={type}
              onChange={(e) =>
                setType(
                  e.target.value as 'market' | 'limit' | 'stop' | 'stop_limit',
                )
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="market">Market</option>
              <option value="limit">Limit</option>
              <option value="stop">Stop</option>
              <option value="stop_limit">Stop Limit</option>
            </select>
          </div>

          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Quantity
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter quantity"
              min="0"
              step="any"
            />
          </div>

          {/* Limit Price */}
          {(type === 'limit' || type === 'stop_limit') && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Limit Price
              </label>
              <input
                type="number"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter limit price"
                min="0"
                step="any"
              />
            </div>
          )}

          {/* Stop Price */}
          {(type === 'stop' || type === 'stop_limit') && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Stop Price
              </label>
              <input
                type="number"
                value={stopPrice}
                onChange={(e) => setStopPrice(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter stop price"
                min="0"
                step="any"
              />
            </div>
          )}

          {/* Duration */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) =>
                setDuration(e.target.value as 'day' | 'gtc' | 'gfd')
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="day">Day</option>
              <option value="gtc">Good Till Cancelled</option>
              <option value="gfd">Good For Day</option>
            </select>
          </div>

          {/* Source Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">
              Fund Source
            </label>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select a source</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.sourceType} ({source.sourceId})
                </option>
              ))}
            </select>
          </div>

          {/* Preview Toggle */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="mb-6 p-4 bg-gray-50 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Order Preview
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Symbol:</span>
                  <span className="font-medium">{symbol.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Side:</span>
                  <span
                    className={`font-medium ${side === 'buy' ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {side.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium">
                    {type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Quantity:</span>
                  <span className="font-medium">{quantity || '-'}</span>
                </div>
                {(type === 'limit' || type === 'stop_limit') && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Limit Price:</span>
                    <span className="font-medium">
                      ${Number(limitPrice || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {(type === 'stop' || type === 'stop_limit') && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Stop Price:</span>
                    <span className="font-medium">
                      ${Number(stopPrice || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Duration:</span>
                  <span className="font-medium">{duration.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Source:</span>
                  <span className="font-medium">
                    {selectedSource
                      ? `${selectedSource.sourceType} (${selectedSource.sourceId})`
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Value:</span>
                  <span className="font-medium">
                    $
                    {(
                      Number(quantity || 0) *
                      Number(limitPrice || stopPrice || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 hover:bg-blue-50 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Preview
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FundOrderModal;
